import React from 'react';
import {
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Button,
	Text,
	VStack,
	HStack,
	Icon,
	Box,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FiCheckCircle, FiUsers, FiMessageCircle } from 'react-icons/fi';

/**
 * Shown once, when every step is finally green.
 *
 * ★ ONLY AFTER THE CHECKS THAT MEAN SOMETHING.
 * A "connected!" modal fired at the end of the connect call would be true and
 * useless: at that moment the number can neither send nor receive and nobody
 * can see it. This waits until a message has gone out AND one has come back,
 * which is the first point at which the claim is honest.
 *
 * ★ IT IS DISMISSED IN THE BROWSER, NOT ON THE SERVER.
 * The architecture stores no setup state anywhere — every step is derived, so a
 * workspace whose people are all removed later is correctly unfinished again.
 * Adding a `readyModalSeen` column would be the first stored setup flag in the
 * system, and it would exist only to suppress a dialog. `localStorage` keyed by
 * the number is the right size for that: a cleared browser shows it once more,
 * which is harmless.
 */

const seenKey = (phoneNumberId) => `wa.ready.${phoneNumberId}`;

/** Whether this number's success dialog has already been dismissed here. */
export function hasSeenReady(phoneNumberId) {
	if (!phoneNumberId) return true;
	try {
		return window.localStorage.getItem(seenKey(phoneNumberId)) === '1';
	} catch (_) {
		// Private windows and blocked site data both throw. Treat it as seen —
		// showing a celebration dialog on every load would be worse than never.
		return true;
	}
}

export function markSeenReady(phoneNumberId) {
	try {
		window.localStorage.setItem(seenKey(phoneNumberId), '1');
	} catch (_) {
		/* nothing to do — see above */
	}
}

const Line = ({ label, value }) => (
	<HStack justify="space-between" spacing="4">
		<Text fontSize="xs" color="text.muted">
			{label}
		</Text>
		<Text fontSize="xs" color="text.primary" textAlign="right" noOfLines={1}>
			{value}
		</Text>
	</HStack>
);

export default function ReadyModal({ channel, account, assignedCount = 0, isOpen, onClose }) {
	return (
		<Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
			<ModalOverlay />
			<ModalContent bg="bg.surface">
				<ModalHeader>
					<HStack spacing="2.5">
						<Icon as={FiCheckCircle} color="success" boxSize="6" />
						<Box>
							<Text fontSize="md" color="text.primary">
								WhatsApp is ready
							</Text>
							<Text fontSize="xs" color="text.muted" fontWeight="normal">
								Messages go out and come back. Nothing left to set up.
							</Text>
						</Box>
					</HStack>
				</ModalHeader>

				<ModalBody>
					<VStack align="stretch" spacing="2" bg="bg.subtle" borderRadius="md" p="3">
						<Line label="Number" value={channel?.displayPhoneNumber ?? channel?.phoneNumberId} />
						{channel?.label && <Line label="Name" value={channel.label} />}
						<Line label="Business account" value={account?.wabaId ?? '—'} />
						<Line label="Sending" value="Working" />
						<Line label="Webhook" value="Verified" />
						<Line label="Receiving" value="Working" />
						<Line
							label="People"
							value={`${assignedCount} assigned`}
						/>
					</VStack>
				</ModalBody>

				<ModalFooter>
					<Button
						as={RouterLink}
						to="/whatsapp/settings?tab=assignments"
						size="sm"
						variant="outline"
						mr="2"
						leftIcon={<Icon as={FiUsers} />}
						onClick={onClose}
					>
						Manage user assignments
					</Button>
					<Button
						as={RouterLink}
						to="/meta-whatsapp"
						size="sm"
						colorScheme="green"
						leftIcon={<Icon as={FiMessageCircle} />}
						onClick={onClose}
					>
						Go to WhatsApp
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
}
